import React, { useState } from "react";
import { L10n, L10nHtml, L10nId } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { useStore } from "../../../models";
import { SubtitleDescription } from "../subtitleDescription";
import { IOrgConfig } from "../../../models/currentUser/OrgConfig";
import commonStyles from "../../../containers/settings/settingsEditCommon.css";
import { observer } from "mobx-react";
import classNames from "classnames";
import styles from "./reportsSettings.css";
import { SettingsForm } from "../settingsForm";
import { EnhancedCheckboxPlaceholder } from "../settingsForms/enhancedCheckboxPlaceholder/enhancedCheckboxPlaceholder";
import { EditInput } from "@fidget/common/components/form/editInput";
import { FieldState, FormState } from "formstate";
import { validateRequiredNumber, validateSeconds } from "@fidget/common/utils/validatorsFormState";
import {
  minutesAndSecondsToMillis,
  minutesFromMillis,
  secondsWithoutMinutesFromMillis
} from "../../../models/utils";
import { staticConfig } from "@fidget/common/config/static-config";

interface Props {
  orgConfig: IOrgConfig;
}

interface BasicSlaFormProps {
  form: BasicSlaFormState;
  onInputClick: () => void;
  upperBoundLabelId: L10nId;
  betweenBoundsLabelId: L10nId;
  lowerBoundLabelId: L10nId;
  isBeingEdited: boolean;
}

type BasicSlaFormFields = { upperBound: FieldState<number>; lowerBound: FieldState<number> };
type BasicSlaFormState = FormState<BasicSlaFormFields>;

const BasicSlaForm = observer(
  ({
    form,
    onInputClick,
    upperBoundLabelId,
    lowerBoundLabelId,
    betweenBoundsLabelId,
    isBeingEdited
  }: BasicSlaFormProps) => {
    const { upperBound, lowerBound } = form.$;

    return (
      <SettingsForm withoutSaveDiscardButtons disabled={!isBeingEdited}>
        <div className={styles["goal-form"]}>
          <p className={styles["goal-header"]}>
            <L10nHtml id={"settings.sla.goal"} />
          </p>
          <div className={styles["goal-row"]}>
            <div className={classNames(styles.marker, styles.good)} />
            <p className={styles["simple-separator"]}>-</p>
            <p>
              <L10nHtml id={lowerBoundLabelId} />
            </p>
            <EditInput
              type={"number"}
              onChange={(ev) => lowerBound.onChange((ev.target as HTMLInputElement).valueAsNumber)}
              min={0}
              value={lowerBound.value}
              className={styles.input}
              onClick={onInputClick}
              errorHint={intl.formatMessage("error.fieldRequired")}
              hasError={form.$.lowerBound.hasError}
            />
          </div>
          <div className={styles["goal-row"]}>
            <div className={classNames(styles.marker, styles.warn)} />
            <p className={styles["simple-separator"]}>-</p>
            <p>
              <L10nHtml id={betweenBoundsLabelId} />
            </p>
          </div>
          <div className={styles["goal-row"]}>
            <div className={classNames(styles.marker, styles.bad)} />
            <p className={styles["simple-separator"]}>-</p>
            <p>
              <L10nHtml id={upperBoundLabelId} />
            </p>
            <EditInput
              type={"number"}
              onChange={(ev) => upperBound.onChange((ev.target as HTMLInputElement).valueAsNumber)}
              min={0}
              value={upperBound.value}
              className={styles.input}
              onClick={onInputClick}
              errorHint={intl.formatMessage("error.fieldRequired")}
              hasError={form.$.upperBound.hasError}
            />
          </div>
        </div>
      </SettingsForm>
    );
  }
);

type ResponseTimeSlaFormFields = {
  upperBoundMinutes: FieldState<number>;
  upperBoundSeconds: FieldState<number>;
  lowerBoundMinutes: FieldState<number>;
  lowerBoundSeconds: FieldState<number>;
};
type ResponseTimeSlaFormState = FormState<ResponseTimeSlaFormFields>;

interface ResponseTimeSlaFormProps {
  form: ResponseTimeSlaFormState;
  onInputClick: () => void;
  upperBoundLabelId: L10nId;
  betweenBoundsLabelId: L10nId;
  lowerBoundLabelId: L10nId;
  isBeingEdited: boolean;
}

const ResponseTimeSlaForm = observer(
  ({
    form,
    onInputClick,
    upperBoundLabelId,
    lowerBoundLabelId,
    betweenBoundsLabelId,
    isBeingEdited
  }: ResponseTimeSlaFormProps) => {
    const { upperBoundMinutes, upperBoundSeconds, lowerBoundMinutes, lowerBoundSeconds } = form.$;

    return (
      <SettingsForm withoutSaveDiscardButtons disabled={!isBeingEdited}>
        <div className={styles["goal-form"]}>
          <p className={styles["goal-header"]}>
            <L10nHtml id={"settings.sla.goal"} />
          </p>
          <div className={styles["goal-row"]}>
            <div className={classNames(styles.marker, styles.good)} />
            <p className={styles["simple-separator"]}>-</p>
            <p>
              <L10nHtml id={lowerBoundLabelId} />
            </p>
            <EditInput
              type={"number"}
              onChange={(ev) =>
                lowerBoundMinutes.onChange((ev.target as HTMLInputElement).valueAsNumber)
              }
              min={0}
              value={lowerBoundMinutes.value}
              className={styles.input}
              onClick={onInputClick}
              errorHint={intl.formatMessage("error.fieldRequired")}
              hasError={form.$.lowerBoundMinutes.hasError}
            />
            <p>min</p>
            <EditInput
              type={"number"}
              onChange={(ev) =>
                lowerBoundSeconds.onChange((ev.target as HTMLInputElement).valueAsNumber)
              }
              min={0}
              value={lowerBoundSeconds.value}
              className={styles.input}
              onClick={onInputClick}
              max={59}
              errorHint={intl.formatMessage("error.seconds")}
              hasError={form.$.lowerBoundSeconds.hasError}
            />
            <p>s</p>
          </div>
          <div className={styles["goal-row"]}>
            <div className={classNames(styles.marker, styles.warn)} />
            <p className={styles["simple-separator"]}>-</p>
            <p>
              <L10nHtml id={betweenBoundsLabelId} />
            </p>
          </div>
          <div className={styles["goal-row"]}>
            <div className={classNames(styles.marker, styles.bad)} />
            <p className={styles["simple-separator"]}>-</p>
            <p>
              <L10nHtml id={upperBoundLabelId} />
            </p>
            <EditInput
              type={"number"}
              onChange={(ev) =>
                upperBoundMinutes.onChange((ev.target as HTMLInputElement).valueAsNumber)
              }
              min={0}
              value={upperBoundMinutes.value}
              className={styles.input}
              onClick={onInputClick}
              max={59}
              errorHint={intl.formatMessage("error.fieldRequired")}
              hasError={form.$.upperBoundMinutes.hasError}
            />
            <p>min</p>
            <EditInput
              type={"number"}
              onChange={(ev) =>
                upperBoundSeconds.onChange((ev.target as HTMLInputElement).valueAsNumber)
              }
              min={0}
              value={upperBoundSeconds.value}
              className={styles.input}
              onClick={onInputClick}
              errorHint={intl.formatMessage("error.seconds")}
              hasError={form.$.upperBoundSeconds.hasError}
            />
            <p>s</p>
          </div>
        </div>
      </SettingsForm>
    );
  }
);

export const SlaSettings = observer(({ orgConfig }: Props) => {
  const [
    waitingConversationsSlaEnabled,
    openedConversationsSlaEnabled,
    averageResponseTimeSlaEnabled,
    onWaitingConversationSlaEnabledChanged,
    onOpenedConversationsSlaEnabled,
    onAverageResponseTimeSlaEnabled,
    submit,
    discard,
    isChanged,
    waitingFormState,
    waitingCurrentlyEditing,
    waitingSlaDiscard,
    waitingFormOnClick,
    openedFormState,
    openedCurrentlyEditing,
    openedSlaDiscard,
    openedFormOnClick,
    responseTimeFormState,
    responseTimeCurrentlyEditing,
    responseTimeSlaDiscard,
    responseTimeFormOnClick,
    waitingSlaSubmit,
    openedSlaSubmit,
    responseTimeSlaSubmit
  ] = slaSettingsState(orgConfig);

  return (
    <>
      <div className={commonStyles.title}>
        <L10n id={"settings.sla.title"} />
      </div>

      <div className={commonStyles.section}>
        <SubtitleDescription
          subtitle={intl.formatMessage("settings.sla.preview")}
          description={intl.formatMessage("settings.sla.description")}
        />
        <SettingsForm disabled={!isChanged} onSubmit={submit} onDiscard={discard}>
          <div className={commonStyles.content}>
            <EnhancedCheckboxPlaceholder
              value={averageResponseTimeSlaEnabled}
              id={"average_response_time_sla_enabled"}
              checkboxLabelId={"settings.sla.activate"}
              titleLabelId={"reports.averageResponseTime.tooltip"}
              descriptionLabelId={"settings.sla.averageResponseTime.description"}
              onCheckboxChange={onAverageResponseTimeSlaEnabled}
              innerFormSubmit={responseTimeSlaSubmit}
              innerFormDiscard={responseTimeSlaDiscard}
              isEditing={responseTimeCurrentlyEditing}
            >
              <ResponseTimeSlaForm
                form={responseTimeFormState}
                onInputClick={responseTimeFormOnClick}
                betweenBoundsLabelId={"settings.sla.averageResponseTime.between"}
                upperBoundLabelId={"settings.sla.averageResponseTime.above"}
                lowerBoundLabelId={"settings.sla.averageResponseTime.below"}
                isBeingEdited={responseTimeCurrentlyEditing}
              />
            </EnhancedCheckboxPlaceholder>

            <EnhancedCheckboxPlaceholder
              value={waitingConversationsSlaEnabled}
              id={"waiting_conversations_sla_enabled"}
              checkboxLabelId={"settings.sla.activate"}
              titleLabelId={"reports.waitingQueue.tooltip"}
              descriptionLabelId={"settings.sla.waitingQueue.description"}
              onCheckboxChange={onWaitingConversationSlaEnabledChanged}
              innerFormSubmit={waitingSlaSubmit}
              innerFormDiscard={waitingSlaDiscard}
              isEditing={waitingCurrentlyEditing}
            >
              <BasicSlaForm
                form={waitingFormState}
                onInputClick={waitingFormOnClick}
                betweenBoundsLabelId={"settings.sla.waitingQueue.between"}
                upperBoundLabelId={"settings.sla.waitingQueue.above"}
                lowerBoundLabelId={"settings.sla.waitingQueue.below"}
                isBeingEdited={waitingCurrentlyEditing}
              />
            </EnhancedCheckboxPlaceholder>

            <EnhancedCheckboxPlaceholder
              value={openedConversationsSlaEnabled}
              id={"opened_conversations_sla_enabled"}
              checkboxLabelId={"settings.sla.activate"}
              titleLabelId={"reports.yoursConversations.tooltip"}
              descriptionLabelId={"settings.sla.openedConversations.description"}
              onCheckboxChange={onOpenedConversationsSlaEnabled}
              innerFormSubmit={openedSlaSubmit}
              innerFormDiscard={openedSlaDiscard}
              isEditing={openedCurrentlyEditing}
            >
              <BasicSlaForm
                form={openedFormState}
                onInputClick={openedFormOnClick}
                betweenBoundsLabelId={"settings.sla.openedConversations.between"}
                upperBoundLabelId={"settings.sla.openedConversations.above"}
                lowerBoundLabelId={"settings.sla.openedConversations.below"}
                isBeingEdited={openedCurrentlyEditing}
              />
            </EnhancedCheckboxPlaceholder>
          </div>
        </SettingsForm>
      </div>
    </>
  );
});

type booleanCallback = (value: boolean) => void;
type normalCallback = () => void;

const basicSlaFormHook = (
  upperBound?: number,
  lowerBound?: number
): [BasicSlaFormState, boolean, normalCallback, normalCallback, normalCallback] => {
  const newForm: BasicSlaFormState = new FormState({
    lowerBound: new FieldState<number>(
      lowerBound !== undefined ? lowerBound : staticConfig.conversationsSlaDefaultRangeLowerBound
    ).validators(validateRequiredNumber),
    upperBound: new FieldState<number>(
      upperBound !== undefined ? upperBound : staticConfig.conversationsSlaDefaultRangeUpperBound
    ).validators(validateRequiredNumber)
  });

  const [formState, setFormState] = useState<BasicSlaFormState>(newForm);
  const [currentlyEditing, setCurrentlyEditing] = useState<boolean>(false);

  const slaDiscard = () => {
    setFormState(newForm);
    setCurrentlyEditing(false);
  };

  const formOnClick = () => {
    setCurrentlyEditing(true);
  };

  const turnOffEditing = () => {
    setCurrentlyEditing(false);
  };

  return [formState, currentlyEditing, slaDiscard, formOnClick, turnOffEditing];
};

const responseTimeSlaFormHook = (
  upperBound?: number,
  lowerBound?: number
): [ResponseTimeSlaFormState, boolean, normalCallback, normalCallback, normalCallback] => {
  const lowerBoundMinutes =
    lowerBound !== undefined
      ? minutesFromMillis(lowerBound)
      : staticConfig.responseTimeDefaultRangeLowerBound;
  const lowerBoundSeconds =
    lowerBound !== undefined ? secondsWithoutMinutesFromMillis(lowerBound, lowerBoundMinutes) : 0;
  const upperBoundMinutes =
    upperBound !== undefined
      ? minutesFromMillis(upperBound)
      : staticConfig.responseTimeDefaultRangeUpperBound;
  const upperBoundSeconds =
    upperBound !== undefined ? secondsWithoutMinutesFromMillis(upperBound, upperBoundMinutes) : 0;

  const newForm: ResponseTimeSlaFormState = new FormState({
    lowerBoundMinutes: new FieldState<number>(lowerBoundMinutes).validators(validateRequiredNumber),
    lowerBoundSeconds: new FieldState<number>(lowerBoundSeconds).validators(validateSeconds),
    upperBoundMinutes: new FieldState<number>(upperBoundMinutes).validators(validateRequiredNumber),
    upperBoundSeconds: new FieldState<number>(upperBoundSeconds).validators(validateSeconds)
  });

  const [formState, setFormState] = useState<ResponseTimeSlaFormState>(newForm);
  const [currentlyEditing, setCurrentlyEditing] = useState<boolean>(false);

  const slaDiscard = () => {
    setFormState(newForm);
    setCurrentlyEditing(false);
  };

  const formOnClick = () => {
    setCurrentlyEditing(true);
  };

  const turnOffEditing = () => {
    setCurrentlyEditing(false);
  };

  return [formState, currentlyEditing, slaDiscard, formOnClick, turnOffEditing];
};

type SlaSettingsHookType = [
  boolean,
  boolean,
  boolean,
  booleanCallback,
  booleanCallback,
  booleanCallback,
  normalCallback,
  normalCallback,
  boolean,
  BasicSlaFormState,
  boolean,
  normalCallback,
  normalCallback,
  BasicSlaFormState,
  boolean,
  normalCallback,
  normalCallback,
  ResponseTimeSlaFormState,
  boolean,
  normalCallback,
  normalCallback,
  normalCallback,
  normalCallback,
  normalCallback
];

const slaSettingsState = (orgConfig: IOrgConfig): SlaSettingsHookType => {
  const { uiStore } = useStore();
  const [isChanged, setIsChanged] = useState<boolean>(false);

  const [waitingConversationsSlaEnabled, setWaitingConversationsSlaEnabled] = useState<boolean>(
    orgConfig.waitingConversationsSlaEnabled || false
  );

  const [openedConversationsSlaEnabled, setOpenedConversationsSlaEnabled] = useState<boolean>(
    orgConfig.openedConversationsSlaEnabled || false
  );

  const [averageResponseTimeSlaEnabled, setAverageResponseTimeSlaEnabled] = useState<boolean>(
    orgConfig.averageResponseTimeSlaEnabled || false
  );

  const [
    waitingFormState,
    waitingCurrentlyEditing,
    waitingSlaDiscard,
    waitingFormOnClick,
    waitingTurnOffEditing
  ] = basicSlaFormHook(
    orgConfig.waitingConversationsSlaRangeUpperBound,
    orgConfig.waitingConversationsSlaRangeLowerBound
  );

  const [
    openedFormState,
    openedCurrentlyEditing,
    openedSlaDiscard,
    openedFormOnClick,
    openedTurnOffEditing
  ] = basicSlaFormHook(
    orgConfig.openedConversationsSlaRangeUpperBound,
    orgConfig.openedConversationsSlaRangeLowerBound
  );

  const [
    responseTimeFormState,
    responseTimeCurrentlyEditing,
    responseTimeSlaDiscard,
    responseTimeFormOnClick,
    responseTimeTurnOffEditing
  ] = responseTimeSlaFormHook(
    orgConfig.averageResponseTimeSlaRangeUpperBound,
    orgConfig.averageResponseTimeSlaRangeLowerBound
  );

  const responseTimeSlaSubmit = async () => {
    try {
      const belowMillis = minutesAndSecondsToMillis(
        responseTimeFormState.$.lowerBoundMinutes.value,
        responseTimeFormState.$.lowerBoundSeconds.value
      );
      const aboveMillis = minutesAndSecondsToMillis(
        responseTimeFormState.$.upperBoundMinutes.value,
        responseTimeFormState.$.upperBoundSeconds.value
      );

      const validated = await responseTimeFormState.validate();
      if (validated.hasError) {
        uiStore.addErrorToast(intl.formatMessage("settings.sla.mustBeSomeNumber"));
        return;
      }
      const isRangeValid = belowMillis < aboveMillis;
      if (!isRangeValid) {
        uiStore.addErrorToast(intl.formatMessage("settings.sla.rangeMustBeValid"));
        return;
      }
      await orgConfig.update({
        averageResponseTimeSlaRangeLowerBound: belowMillis,
        averageResponseTimeSlaRangeUpperBound: aboveMillis
      });
      responseTimeTurnOffEditing();
    } catch (e) {
      uiStore.addErrorToast(intl.formatMessage("error.something"));
    }
  };

  const openedSlaSubmit = async () => {
    try {
      const validated = await openedFormState.validate();
      if (validated.hasError) {
        uiStore.addErrorToast(intl.formatMessage("settings.sla.mustBeSomeNumber"));
        return;
      }
      const isRangeValid = openedFormState.$.lowerBound.value < openedFormState.$.upperBound.value;
      if (!isRangeValid) {
        uiStore.addErrorToast(intl.formatMessage("settings.sla.rangeMustBeValid"));
        return;
      }
      await orgConfig.update({
        openedConversationsSlaRangeLowerBound: openedFormState.$.lowerBound.value,
        openedConversationsSlaRangeUpperBound: openedFormState.$.upperBound.value
      });
      openedTurnOffEditing();
    } catch (e) {
      uiStore.addErrorToast(intl.formatMessage("error.something"));
    }
  };

  const waitingSlaSubmit = async () => {
    try {
      const validated = await waitingFormState.validate();
      if (validated.hasError) {
        uiStore.addErrorToast(intl.formatMessage("settings.sla.mustBeSomeNumber"));
        return;
      }
      const isRangeValid =
        waitingFormState.$.lowerBound.value < waitingFormState.$.upperBound.value;
      if (!isRangeValid) {
        uiStore.addErrorToast(intl.formatMessage("settings.sla.rangeMustBeValid"));
        return;
      }
      await orgConfig.update({
        waitingConversationsSlaRangeLowerBound: waitingFormState.$.lowerBound.value,
        waitingConversationsSlaRangeUpperBound: waitingFormState.$.upperBound.value
      });
      waitingTurnOffEditing();
    } catch (e) {
      uiStore.addErrorToast(intl.formatMessage("error.something"));
    }
  };

  const submit = async () => {
    try {
      await orgConfig.update({
        waitingConversationsSlaEnabled,
        openedConversationsSlaEnabled,
        averageResponseTimeSlaEnabled
      });
      setIsChanged(false);
    } catch (e) {
      uiStore.addErrorToast(intl.formatMessage("error.something"));
    }
  };

  const onWaitingConversationSlaEnabledChanged = (checked: boolean) => {
    setWaitingConversationsSlaEnabled(checked);
    setIsChanged(true);
  };

  const onOpenedConversationsSlaEnabled = (checked: boolean) => {
    setOpenedConversationsSlaEnabled(checked);
    setIsChanged(true);
  };

  const onAverageResponseTimeSlaEnabled = (checked: boolean) => {
    setAverageResponseTimeSlaEnabled(checked);
    setIsChanged(true);
  };

  const discard = () => {
    setWaitingConversationsSlaEnabled(orgConfig.waitingConversationsSlaEnabled || false);
    setOpenedConversationsSlaEnabled(orgConfig.openedConversationsSlaEnabled || false);
    setAverageResponseTimeSlaEnabled(orgConfig.averageResponseTimeSlaEnabled || false);
    setIsChanged(false);
  };

  return [
    waitingConversationsSlaEnabled,
    openedConversationsSlaEnabled,
    averageResponseTimeSlaEnabled,
    onWaitingConversationSlaEnabledChanged,
    onOpenedConversationsSlaEnabled,
    onAverageResponseTimeSlaEnabled,
    submit,
    discard,
    isChanged,
    waitingFormState,
    waitingCurrentlyEditing,
    waitingSlaDiscard,
    waitingFormOnClick,
    openedFormState,
    openedCurrentlyEditing,
    openedSlaDiscard,
    openedFormOnClick,
    responseTimeFormState,
    responseTimeCurrentlyEditing,
    responseTimeSlaDiscard,
    responseTimeFormOnClick,
    waitingSlaSubmit,
    openedSlaSubmit,
    responseTimeSlaSubmit
  ];
  // tslint:disable-next-line:max-file-line-count
};
